/**
 * Help and feedback destinations for the side navigation.
 *
 * Kept in one place so a destination can be changed without touching the layout,
 * and so the rules below are enforced once rather than per link.
 *
 * Rules:
 * - A nav entry with no destination is not rendered. A link that looks available
 *   and then 404s is worse than an absent one, so anything unconfigured is
 *   dropped rather than pointed at a plausible guess.
 * - Whether a link is external is *derived* from its destination, not declared,
 *   so the two cannot disagree. That matters because the side nav intercepts
 *   clicks for in-app routing and must let external links through untouched.
 */

export interface HelpLinkSource {
  id: string;
  text: string;
  /** Empty means "not available" — the link is omitted from the nav. */
  href: string;
  /** Optional nav badge, e.g. to mark a stub as unfinished. */
  badge?: string;
}

export interface HelpLink extends HelpLinkSource {
  external: boolean;
}

/**
 * The Taskei room that actually tracks work on this app. Verified to resolve.
 * Filing a bug and requesting a feature both land here; Taskei has no verified
 * deep link that pre-opens a typed form, so the labels say where you end up
 * rather than promising a form that may not appear.
 */
const TASKEI_ROOM = 'https://taskei.amazon.dev/rooms/2fb8d137-cc42-473b-b2d3-a8035135eb33';

export const HELP_LINK_SOURCES: HelpLinkSource[] = [
  // No guide is written yet. Stubbed rather than hidden, so the nav reflects what
  // is planned — but pointed at an in-app placeholder instead of a URL that would
  // 404, and badged so nobody expects content that does not exist.
  { id: 'user-guide', text: 'User guide', href: '/help', badge: 'Coming soon' },
  { id: 'report-bug', text: 'Report a bug', href: TASKEI_ROOM },
  { id: 'feature-request', text: 'Feature request', href: TASKEI_ROOM },
];

export function buildHelpLinks(sources: HelpLinkSource[]): HelpLink[] {
  return sources
    .filter((s) => s.href.trim() !== '')
    .map((s) => ({ ...s, external: /^https?:\/\//i.test(s.href.trim()) }));
}

/** The links actually rendered in the nav. */
export const HELP_LINKS: HelpLink[] = buildHelpLinks(HELP_LINK_SOURCES);
